// GtSearchPalette — search everything, from anywhere (C1-SEARCH, 25 Oct 2026).
// Opened from Search in the left rail, or Cmd+K / Ctrl+K anywhere in the app.
// Results come grouped (candidates, roles, clients, contacts, benches, forms,
// shares), each with why it matched. Arrow keys move, Enter opens, Esc closes.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import API_BASE from '../config';
import { SearchMatchLine, SearchTips, AiReading } from './gtSearch';

const ICONS = {
  candidate: 'M16 19v-1a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v1 M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  role: 'M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2 M4 7h16a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a1 1 0 0 1 1-1z M3 12h18',
  client: 'M3 21h18 M5 21V7l8-4v18 M19 21V11l-6-3 M9 9v.01 M9 12v.01 M9 15v.01 M9 18v.01',
  contact: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M9 12a2.2 2.2 0 1 0 0-4.4A2.2 2.2 0 0 0 9 12z M5.5 16.5a3.5 3.5 0 0 1 7 0 M15 9h3 M15 12.5h3',
  bench: 'M12 3 3 8l9 5 9-5-9-5z M3 12.5l9 5 9-5 M3 17l9 5 9-5',
  form: 'M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1z M8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M8 11h8 M8 15h5',
  share: 'M22 2 11 13 M22 2l-7 20-4-9-9-4 20-7z',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z M21 21l-4.3-4.3',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 2',
  arrow: 'M5 12h14 M13 6l6 6-6 6',
};
function Ico({ d, className }) {
  return (
    <svg className={className || 'gtsx-ic'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {d.split(' M').map((p, i) => <path key={i} d={(i ? 'M' : '') + p} />)}
    </svg>
  );
}

const AVATAR_COLOURS = ['#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C', '#C2410C', '#374151', '#9D174D', '#1565C0', '#4E342E', '#5B6C1E', '#0F766E', '#B91C1C', '#5D4037', '#3949AB', '#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVATAR_COLOURS[h % AVATAR_COLOURS.length]; }
function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }

const RECENT_KEY = 'gt.search.recent';
function readRecent() { try { const v = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(v) ? v.filter(x => typeof x === 'string').slice(0, 6) : []; } catch (e) { return []; } }
function saveRecent(q) {
  const t = String(q || '').trim();
  if (!t) return;
  try { window.localStorage.setItem(RECENT_KEY, JSON.stringify([t].concat(readRecent().filter(x => x !== t)).slice(0, 6))); } catch (e) { /* private window: fine */ }
}

export default function GtSearchPalette({ open, onClose, getToken, navigate }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(0);
  const [recent, setRecent] = useState(readRecent);
  const [showTips, setShowTips] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setRecent(readRecent());
    setSel(0);
    const t = setTimeout(() => { if (inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, 10);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const text = q.trim();
    if (!open) return undefined;
    if (!text) { setData(null); setLoading(false); setError(null); return undefined; }
    const my = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const token = await getToken();
        const res = await fetch(API_BASE + '/api/search?limit=5&q=' + encodeURIComponent(text), { headers: { Authorization: 'Bearer ' + token } });
        const body = await res.json().catch(() => ({}));
        if (my !== seq.current) return;
        if (!res.ok) throw new Error(body.error || 'Search failed');
        setData(body); setError(null); setSel(0);
      } catch (e) { if (my === seq.current) { setError(e.message); setData(null); } }
      finally { if (my === seq.current) setLoading(false); }
    }, 160);
    return () => clearTimeout(t);
  }, [q, open, getToken]);

  // One flat list for the keyboard: every result, plus each "see all" row.
  const flat = useMemo(() => {
    const out = [];
    ((data && data.groups) || []).forEach((g) => {
      g.items.slice(0, g.type === 'candidate' ? 7 : 5).forEach((it) => { if (it.url) out.push({ kind: 'item', group: g, it }); });
      if (g.more && g.total > g.items.length) out.push({ kind: 'more', group: g });
    });
    return out;
  }, [data]);

  const go = useCallback((entry) => {
    if (!entry) return;
    saveRecent(q);
    const url = entry.kind === 'more' ? entry.group.more : entry.it.url;
    onClose();
    if (url) navigate(url);
  }, [q, navigate, onClose]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector('[data-sel="1"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  function onKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, Math.max(flat.length - 1, 0))); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); go(flat[sel]); }
  }

  if (!open) return null;
  const text = q.trim();
  let idx = -1;
  const groups = (data && data.groups) || [];

  return (
    <div className="gtsx-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gtsx-panel" role="dialog" aria-modal="true" aria-label="Search everything">
        <div className="gtsx-inputrow">
          <Ico d={ICONS.search} className="gtsx-inputic" />
          <input ref={inputRef} className="gtsx-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search people, roles, clients, benches… or describe who you need" aria-label="Search everything"
            autoComplete="off" spellCheck="false" />
          {loading ? <span className="gtsx-spin" aria-label="Searching" /> : <kbd className="gtsx-kbd">esc</kbd>}
        </div>
        {data && data.ai && text && (
          <div className="gtsx-readas gtsx-readas--ai">
            <AiReading ai={data.ai} onQuery={(t) => { setQ(t); if (inputRef.current) inputRef.current.focus(); }} />
          </div>
        )}
        {data && !data.ai && (data.interpreted || data.description) && text && (
          <div className="gtsx-readas">
            {data.description && <span className="gtsx-chip"><span className="gtsx-dot" />{data.meaning_available ? 'Searching by meaning' : 'Matching on words'}</span>}
            {data.interpreted && <span>Read as <b>{data.interpreted}</b></span>}
          </div>
        )}

        <div className="gtsx-body" ref={listRef}>
          {!text && (
            <div className="gtsx-start">
              {recent.length > 0 && (
                <div className="gtsx-group">
                  <div className="gtsx-ghead"><span>Recent searches</span></div>
                  {recent.map((r) => (
                    <button type="button" key={r} className="gtsx-row gtsx-row--recent" onClick={() => setQ(r)}>
                      <span className="gtsx-av gtsx-av--ic"><Ico d={ICONS.clock} /></span>
                      <span className="gtsx-rbody"><span className="gtsx-rtitle">{r}</span></span>
                    </button>
                  ))}
                </div>
              )}
              <SearchTips />
            </div>
          )}

          {text && error && <div className="gtsx-empty">{error}</div>}
          {text && !error && data && groups.length === 0 && !loading && (
            <div className="gtsx-empty">
              <div className="gtsx-empty-t">Nothing found for “{text}”.</div>
              <div className="gtsx-empty-s">Try fewer words, OR between alternatives, or describe the person in a sentence.</div>
              {data.indexing > 0 && <div className="gtsx-empty-s gtsx-empty-idx">The search index is still being built: {Number(data.indexing).toLocaleString('en-GB')} records to go. Names, emails and IDs are found straight away; other words may miss people until it finishes.</div>}{/* C1-SEARCHFAST */}
              <button type="button" className="gtsx-hint-btn" onClick={() => setShowTips(v => !v)}>{showTips ? 'Hide search tips' : 'Search tips'}</button>
              {showTips && <SearchTips compact />}
            </div>
          )}

          {text && groups.map((g) => (
            <div className="gtsx-group" key={g.type}>
              <div className="gtsx-ghead">
                <span>{g.label}</span>
                <span className="gtsx-gcount">{g.total}</span>
              </div>
              {g.items.slice(0, g.type === 'candidate' ? 7 : 5).filter(it => it.url).map((it) => {
                idx += 1;
                const me = idx;
                const on = me === sel;
                const person = g.type === 'candidate' || g.type === 'contact';
                return (
                  <button type="button" key={g.type + it.id} data-sel={on ? '1' : '0'}
                    className={'gtsx-row' + (on ? ' gtsx-row--on' : '')}
                    onMouseMove={() => { if (sel !== me) setSel(me); }} onClick={() => go({ kind: 'item', group: g, it })}>
                    {person
                      ? <span className="gtsx-av" style={{ background: avatarColour(it.title) }}>{initials(it.title)}</span>
                      : <span className="gtsx-av gtsx-av--ic"><Ico d={ICONS[g.type] || ICONS.search} /></span>}
                    <span className="gtsx-rbody">
                      <span className="gtsx-rtitle">{it.title}{it.ref && <span className="gtsx-ref">{it.ref}</span>}</span>
                      {it.sub && <span className="gtsx-rsub">{it.sub}</span>}
                      {it.match && <SearchMatchLine m={it.match} />}
                    </span>
                    {on && <Ico d={ICONS.arrow} className="gtsx-go" />}
                  </button>
                );
              })}
              {g.more && g.total > g.items.length && (() => {
                idx += 1;
                const me = idx;
                const on = me === sel;
                return (
                  <button type="button" data-sel={on ? '1' : '0'} className={'gtsx-more' + (on ? ' gtsx-more--on' : '')}
                    onMouseMove={() => { if (sel !== me) setSel(me); }} onClick={() => go({ kind: 'more', group: g })}>
                    See all {g.total} {g.label.toLowerCase()} in the list <Ico d={ICONS.arrow} className="gtsx-moreic" />
                  </button>
                );
              })()}
            </div>
          ))}
        </div>

        <div className="gtsx-foot">
          <span><kbd className="gtsx-kbd">↑</kbd><kbd className="gtsx-kbd">↓</kbd> move</span>
          <span><kbd className="gtsx-kbd">↵</kbd> open</span>
          <span><kbd className="gtsx-kbd">esc</kbd> close</span>
          {data && data.took_ms != null && text && <span className="gtsx-took">{data.took_ms} ms</span>}
        </div>
      </div>
    </div>
  );
}
